import type { ConstructorPlan, ConstructorStage } from '../constructorTypes'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import type { AuthSession } from '../../../../types/plans'
import type { MisImportedPlanSummary } from '../auto-distribute/selectedPlanServices'
import { WindowDockProvider, WindowShell } from './windowing'
import { useConstructorWorkflow } from './ConstructorWorkflowContext'
import { useConstructorDiagnosticsDraft } from './ConstructorDiagnosticsDraftContext'
import { PlanDiagnosticsReviewPanel } from '../diagnostics/PlanDiagnosticsReviewPanel'
import { PlanVoiceFillPanel } from '../voice-fill/PlanVoiceFillPanel'
import { PlanAutoDistributePanel } from '../auto-distribute/PlanAutoDistributePanel'
import { VoiceComposerPanel } from '../voice/VoiceComposerPanel'
import { GammaPresentationStep } from '../presentation/GammaPresentationStep'
import { PlanGammaPresentationStep } from '../diagnostics/PlanGammaPresentationStep'
import { getServices } from '../constructorTypes'

const UI_SURFACE = 'staff_lk_constructor'

type Props = {
  planDetail: ConstructorPlan
  stages: ConstructorStage[]
  totalAmount: number
  bootstrap: TenantBootstrapModel
  session: AuthSession
  patientName: string
  misPatientId: string | null
  misImportedSummaries: MisImportedPlanSummary[]
  onReload: () => void
}

export function ConstructorWorkflowWindows({
  planDetail,
  stages,
  totalAmount,
  bootstrap,
  session,
  patientName,
  misPatientId,
  misImportedSummaries,
  onReload,
}: Props) {
  const { windows, openWindow, closeWindow, minimizeWindow } = useConstructorWorkflow()
  const draft = useConstructorDiagnosticsDraft()

  const staffUserId =
    session.user?.id != null && String(session.user.id).trim() !== ''
      ? String(session.user.id)
      : ''

  const appliedMisPlanId = draft.proposal?.applied_mis_plan_id ?? planDetail.mis_plan_id ?? null

  return (
    <WindowDockProvider>
      <WindowShell
        open={windows['plan-review'].open}
        windowId="plan-review"
        title="Проверка и диалог с ИИ"
        description="Плавающее окно проверки: этапы, голосовая/текстовая корректировка, утверждение."
        allowMinimize
        allowFullscreen
        onClose={() => closeWindow('plan-review')}
        initialWidth={1320}
        initialHeight={860}
        minWidth={920}
        minHeight={600}
      >
        {draft.proposal && draft.misPatientId && draft.bootstrap && draft.session ? (
          <PlanDiagnosticsReviewPanel
            apiBase={draft.bootstrap.apiBase}
            session={draft.session}
            misPatientId={draft.misPatientId}
            staffUserId={draft.staffUserId || staffUserId}
            uiSurface={UI_SURFACE}
            proposal={draft.proposal}
            onProposalChange={draft.setProposal}
            onApplied={(data) => void draft.applyHandler?.(data)}
          />
        ) : (
          <p className="p-4 text-sm text-slate-500">Сначала составьте план лечения в правой панели (КТ → Diagnocat).</p>
        )}
      </WindowShell>

      <WindowShell
        open={windows['plan-voice-fill'].open}
        windowId="plan-voice-fill"
        title="Голос / текст — план"
        onClose={() => closeWindow('plan-voice-fill')}
        initialWidth={720}
        initialHeight={520}
        allowMinimize
      >
        <PlanVoiceFillPanel
          plan={planDetail}
          session={session}
          apiBase={bootstrap.apiBase}
          misPatientId={misPatientId}
          staffUserId={staffUserId}
          diagnosticsLinked
          onApplied={onReload}
        />
      </WindowShell>

      <WindowShell
        open={windows['plan-auto-distribute'].open}
        windowId="plan-auto-distribute"
        title="ИИ-разнесение по этапам"
        onClose={() => closeWindow('plan-auto-distribute')}
        initialWidth={800}
        initialHeight={600}
        allowMinimize
      >
        <PlanAutoDistributePanel
          plan={planDetail}
          stages={stages}
          session={session}
          apiBase={bootstrap.apiBase}
          patientName={patientName}
          misPatientId={misPatientId}
          misImportedSummaries={misImportedSummaries}
          onApplied={onReload}
        />
      </WindowShell>

      <WindowShell
        open={windows['plan-voice-composer'].open}
        windowId="plan-voice-composer"
        title="Голосовой композер"
        onClose={() => closeWindow('plan-voice-composer')}
        initialWidth={800}
        initialHeight={600}
        allowMinimize
      >
        <VoiceComposerPanel plan={planDetail} stages={stages} session={session} apiBase={bootstrap.apiBase} onApplied={onReload} />
      </WindowShell>

      <WindowShell
        open={windows['plan-presentation'].open}
        windowId="plan-presentation"
        title="Презентация Gamma"
        onClose={() => closeWindow('plan-presentation')}
        initialWidth={880}
        initialHeight={640}
        allowMinimize
      >
        {misPatientId && appliedMisPlanId ? (
          <PlanGammaPresentationStep bootstrap={bootstrap} session={session} misPatientId={misPatientId} planId={appliedMisPlanId} planDetail={planDetail} />
        ) : (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            Сначала примените план в MIS. После создания MIS-плана Gamma возьмёт фото NAS по созданному plan_id.
          </div>
        )}
      </WindowShell>

      <WindowShell
        open={windows['plan-tools'].open}
        windowId="plan-tools"
        title="Инструменты плана"
        onClose={() => closeWindow('plan-tools')}
        initialWidth={900}
        initialHeight={700}
        allowMinimize
      >
        <div className="grid gap-3 p-2">
          <button type="button" className="rounded border px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={() => openWindow('plan-review')}>
            Черновик / этапы
          </button>
          <button type="button" className="rounded border px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={() => openWindow('plan-voice-fill')}>
            Голос / текст
          </button>
          <button type="button" className="rounded border px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={() => openWindow('plan-auto-distribute')}>
            Разнесение по этапам
          </button>
          <button type="button" className="rounded border px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={() => openWindow('plan-presentation')}>
            Gamma
          </button>
          <dl className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
            <div className="flex justify-between">
              <dt className="text-slate-500">Этапов</dt>
              <dd className="font-medium text-slate-700">{stages.length}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Услуг</dt>
              <dd className="font-medium text-slate-700">{stages.flatMap(getServices).length}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Итого</dt>
              <dd className="font-semibold text-slate-800">{totalAmount.toLocaleString('ru-RU')} ₽</dd>
            </div>
          </dl>
        </div>
      </WindowShell>
    </WindowDockProvider>
  )
}
